/**
 * Public collection `/l/<token>` for anonymous viewers (SPEC §22.3): reels, creators, metrics, scores and the
 * owner's notes. No captions, hooks or transcripts (sign-in); reels play through Instagram's embed.
 */
import { plural } from '@vw/core/src/plural';
import { LINK_GONE_TITLE } from '../../public-route';
import { useEffect, useState } from 'react';
import { ExternalLink, Link2Off, Lock, X } from 'lucide-react';
import { BRAND } from '@vw/core/src/brand';
import { EmptyState, ErrorState, Spinner, formatWiseScore } from '@vw/ui';
import { api, ApiError, type CollectionView, type ItemReel } from '../../api';
import { compact, igReel } from '../../format';
import { InstagramEmbed } from '../../components/InstagramEmbed';
import { PublicShell, signInHref } from './PublicShell';

export function PublicCollection({ token }: { token: string }) {
  const [c, setC] = useState<CollectionView | null>(null);
  const [gone, setGone] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState<ItemReel | null>(null);
  useEffect(() => {
    setFailed(null);
    // Only a 404 means the link is gone; anything else (rate limit, network) can be retried.
    api<CollectionView>(`/api/public/collections/${token}`).then(setC, (e) => (e instanceof ApiError && e.status === 404 ? setGone(true) : setFailed((e as Error).message)));
  }, [token, attempt]);
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open]);
  const cta = { label: `Sign in to ${BRAND.name}`, href: signInHref(`/l/${token}`) };
  if (gone) return <PublicShell cta={cta}><EmptyState icon={<Link2Off className="h-7 w-7" />} title={LINK_GONE_TITLE}>If it was shared only with signed-in users, sign in to open it.</EmptyState></PublicShell>;
  if (failed) return <PublicShell cta={null}><ErrorState message={`Could not load this collection: ${failed}`} onRetry={() => setAttempt((n) => n + 1)} /></PublicShell>;
  if (!c) return <PublicShell cta={null}><Spinner label="Loading collection…" /></PublicShell>;

  return (
    <PublicShell cta={cta}>
      <section className="flex items-start gap-4 rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-secondary text-4xl">{c.emoji ?? '📁'}</span>
        <div className="min-w-0 space-y-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-primary">Reel collection</p>
          <h1 className="text-2xl font-black">{c.name}</h1>
          {c.description && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{c.description}</p>}
          <p className="text-xs text-muted-foreground">{plural(c.items.length, 'reel')}{c.ownerName ? ` · collected by ${c.ownerName}` : ''}</p>
        </div>
      </section>

      <ol className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {c.items.map((it, i) => (
          <li key={it.reelPk} className="overflow-hidden rounded-2xl border border-border bg-card shadow-xs">
            {it.reel ? (
              <button onClick={() => setOpen(it.reel)} className="block w-full text-left">
                <div className="relative aspect-[9/16] bg-secondary">
                  {it.reel.thumbUrl && <img src={it.reel.thumbUrl} alt="" referrerPolicy="no-referrer" loading="lazy" className="h-full w-full object-cover" />}
                  <span className="absolute left-2 top-2 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-bold text-white">#{i + 1}</span>
                  {it.reel.outlierScore !== null && <span className="absolute right-2 top-2 rounded-md bg-amber-400 px-1.5 py-0.5 text-[11px] font-black text-black">{formatWiseScore(it.reel.outlierScore)}</span>}
                </div>
                <div className="space-y-1 p-3 text-xs">
                  <p className="font-bold">@{it.reel.ownerHandle}</p>
                  <p className="text-muted-foreground">▶ {compact(it.reel.views)} · ❤ {it.reel.likesHidden ? '–' : compact(it.reel.likes)} · 💬 {compact(it.reel.comments)}</p>
                  {it.note && <p className="rounded-lg bg-primary/5 px-2 py-1 text-foreground">{it.note}</p>}
                </div>
              </button>
            ) : <p className="p-4 text-xs italic text-muted-foreground">This reel is no longer available.</p>}
          </li>
        ))}
      </ol>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-3 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && setOpen(null)}>
          <div role="dialog" aria-modal="true" aria-label="Reel" className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-y-auto rounded-2xl bg-card md:flex-row md:overflow-hidden">
            <button aria-label="Close" onClick={() => setOpen(null)} className="absolute right-3 top-3 z-10 rounded-full bg-black/60 p-1.5 text-white"><X className="h-4 w-4" /></button>
            <InstagramEmbed shortcode={open.shortcode} className="mx-auto aspect-[9/16] max-h-[60vh] w-full shrink-0 border-0 bg-white md:max-h-none md:w-[55%]" />
            <div className="space-y-3 p-5">
              <p className="font-bold">@{open.ownerHandle}</p>
              <p className="text-sm text-muted-foreground">Wise Score {open.outlierScore !== null ? formatWiseScore(open.outlierScore) : '–'} · ▶ {compact(open.views)}</p>
              <div className="space-y-2 rounded-2xl border border-border bg-secondary/50 p-4">
                <p className="flex items-center gap-2 text-sm font-bold"><Lock className="h-4 w-4" />Spoken hook & transcript</p>
                <p className="text-xs text-muted-foreground">Sign in free to read the hook and full transcript of this reel.</p>
                <a href={cta.href} className="inline-block rounded-xl bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground">Sign in free</a>
              </div>
              <a href={igReel(open.shortcode)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary">Open on Instagram<ExternalLink className="h-3 w-3" /></a>
            </div>
          </div>
        </div>
      )}
    </PublicShell>
  );
}
